import type { paths } from '@forgd/openapi/api'
import type { EntryKey } from '@pinia/colada'
import type { NitroFetchOptions } from 'nitropack'
import type { ZodIssue } from 'zod'
import type {
  PathMethodParams,
  SuccessResponseBody,
  ValidMethods,
} from '../types/api'
import { withTrailingSlash } from 'ufo'
import { deepUnref } from '../utils/vue'
import { useDevLogger } from './useDevLogger'

export type { SuccessResponseBody } from '../types/api'

interface ApiOptions {
  // optional flag to enable error toast, defaults to false
  toast?: boolean
  /**
   * Pinia Colada will remove data in between requests, which causes our UI
   * to jump around between loading states.
   *
   * Enabling this flag will wait for the fetch to finish before updating data.
   */
  stableData?: boolean
}

interface ParsedApiFetchParams {
  path?: Record<string, unknown>
  query?: Record<string, unknown>
  body?: unknown
  header?: Record<string, unknown>
}

export interface QueryConfig<P extends keyof paths, M extends ValidMethods<P>> {
  method?: M extends ValidMethods<P> ? M : never
  params?: MaybeRefOrGetter<PathMethodParams<P, M>>
  options?: MaybeRefOrGetter<ApiOptions>
  key?: EntryKey | (() => EntryKey)
}

const errors = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  500: 'Internal Server Error',
  501: 'Not Implemented',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
  505: 'HTTP Version Not Supported',
  506: 'Variant Also Negotiates',
  507: 'Insufficient Storage',
  509: 'Bandwidth Limit Exceeded',
  510: 'Not Extended',
}

export class ApiError extends Error {
  status?: number
  title?: string
  output?: any
  data?: any

  constructor(message?: string, title?: string, output?: any, status?: number, data?: any) {
    super(message)
    this.status = status
    this.title = title
    this.output = output
    this.data = data
  }
}

function parseZodError({ issues }: { issues: ZodIssue[] }) {
  const desc = plural(issues, 'issue')
  const text = issues.map((issue: ZodIssue) => {
    return `  - ${issue.path[0]} : ${issue.message}`
  }).join('\n')
  return { desc, text: `\n\n${text}\n\n` }
}

function parseError(error: any, status: number) {
  // defaults
  const title = errors[status as keyof typeof errors] || 'Error'
  let description = error?.message
  let output: any = error

  // zod errors
  if (error.paramsResult) {
    const { desc, text } = parseZodError(error.paramsResult)
    description = `Invalid parameters (${desc})`
    output = text
  }
  else if (error.queryResult) {
    const { desc, text } = parseZodError(error.queryResult)
    description = `Invalid query (${desc})`
    output = text
  }
  // we need to normalize messages like below as way too long
  // [ts-rest] Response validation failed for GET <endpoint>:
  // [\n  {\n    \"code\": \"invalid_type\",\n    \"expected\": \"number\",\n    \"received\": \"null\",\n
  else if (description && description?.startsWith('[ts-rest] Response validation failed for')) {
    const payload = description.substring(description.indexOf(': ') + 2).trim()
    try {
      const parsed = JSON.parse(payload)
      // parsed in an array of objects, this could be hundreds of errors so we just return the first one
      if (Array.isArray(parsed) && parsed.length > 0) {
        const firstError = parsed[0]
        description = firstError.message || 'Response validation failed'
        output = firstError
      }
      else {
        description = 'Response validation failed'
        output = parsed
      }
    }
    catch (e) {
      // if parsing fails, we just keep the original description
      console.error('Failed to parse ts-rest error', e)
    }
  }

  return {
    title,
    description,
    output,
  }
}

export function parseApiFetchParams(params: MaybeRefOrGetter<unknown>): ParsedApiFetchParams {
  return deepUnref(toValue(params)) as ParsedApiFetchParams
}

function getUrl<P extends keyof paths>(path: P, params?: MaybeRefOrGetter<unknown>) {
  const parsedParams = params ? parseApiFetchParams(params) : undefined
  let interpolatedPath = path as string

  if (parsedParams?.path) {
    for (const [key, value] of Object.entries(parsedParams.path)) {
      if (typeof value !== 'string' || value.length === 0) {
        const logger = useDevLogger('useApiQuery')
        logger.error('Path parameter is undefined', path, { params: parsedParams })
        throw new ApiError('Path parameter is undefined', 'Invalid Path Parameter', parsedParams.path, 400)
      }
      interpolatedPath = interpolatedPath.replace(`{${key}}`, encodeURIComponent(value as string))
    }
  }
  return interpolatedPath
}

function createFetchClient<P extends keyof paths, M extends ValidMethods<P>>(path: P, options?: MaybeRefOrGetter<ApiOptions>) {
  const logger = useDevLogger('useApiQuery')
  const runtimeConfig = useRuntimeConfig()
  const supabase = useSupabaseClient()
  const baseURL = withTrailingSlash(runtimeConfig.public.apiUrl)

  return $fetch.create<SuccessResponseBody<P, M>>({
    baseURL,
    // send first-party cookies (e.g. fgclick attribution) to the API subdomain
    credentials: 'include',
    async onRequest({ options }) {
      const { data: { session } } = await supabase.auth.getSession()

      if (session) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${session.access_token}`,
        } as typeof options.headers & { Authorization: string }
      }
      else {
        // TODO: we should not even try making a request if session is missing unless we are using a public endpoint
        // usually frontend keeps hardcoded public endpoints or there is a specific path for public endpoints like /api/public/...
        // and then if the endpoint that requires auth is called, we could skip it completely and redirect directly to login page
        // but for now lets just keep the current behavior and call the endpoint and expect the client to handle it
      }

      logger.debug('onRequest', path, { options })
      // This ensures arrays in query params are serialized in a way that is compatible with the backend
      if (options?.query) {
        for (const key in options.query) {
          if (Array.isArray(options.query[key])) {
            for (const [index, value] of options.query[key].entries()) {
              options.query[`${key}[${index}]`] = value
            }
            delete options.query[key]
          }
        }
        logger.debug('onRequest:query', { query: options.query })
      }
    },
    onResponse({ response }) {
      logger.debug('onResponse', path, { response })
      if (response.status < 300) {
        return response._data
      }
      return null
    },
    onRequestError({ error }) {
      logger.error('onRequestError', path, { error })
    },
    async onResponseError({ response }) {
      logger.error('onResponseError', path, { response })

      const nuxtApp = useNuxtApp()
      const toast = useAppToast()
      const auth = useAuth()

      // Create error object to propagate
      const errorData = response._data as object
      const { title, description, output } = parseError(errorData, response.status)
      const enhancedError = new ApiError(description, title, output, response.status, errorData)

      // unauthorised - pop teammate access wall
      if (response.status === 403) {
        await nuxtApp.hooks.callHook('forgd:fetch:unauthorized', { args: null, res: response._data })
      }
      else if (response.status === 401 && auth.isLoggedIn) {
        await auth.logout('expired')
      }
      // anything else
      else {
        // developer
        // eslint-disable-next-line no-console
        console.log(`\n${title}\n\n  > ${path}`, output)

        // user
        if (toValue(options)?.toast) {
          const fn = response?.status < 400 ? toast.warning : toast.error
          fn({ title, description })
        }
      }

      // Propagate error to be caught by .catch()
      throw enhancedError
    },
  })
}

/**
 * Equivalent of a typed $fetch call, using forgd contract types
 *
 *  @example
 *  const { data } = await apiFetch('/projects/{projectId}', {
 *    path: {
 *      projectId: '123',
 *    },
 *  })
 */
export async function apiFetch<P extends keyof paths, M extends ValidMethods<P>>(
  path: P,
  config: QueryConfig<P, M>,
) {
  const fetchClient = createFetchClient<P, M>(path, config.options) as (
    url: string,
    options: NitroFetchOptions<keyof paths, ValidMethods<P>>,
  ) => Promise<SuccessResponseBody<P, M>>
  const parsedParams = config.params ? parseApiFetchParams(config.params) : undefined
  return fetchClient(getUrl(path, parsedParams), {
    method: config.method,
    ...parsedParams,
  } as NitroFetchOptions<keyof paths, ValidMethods<P>>)
}
